import { useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Send, CheckCircle, AlertCircle, Loader2, Briefcase } from 'lucide-react';
import { useContactForm } from '../../../hooks/useContactForm';
import '../../../styles/contact.css';

const backdropVariants = {
  hidden:  { opacity: 0 },
  visible: { opacity: 1 },
  exit:    { opacity: 0 },
};

const modalVariants = {
  hidden:  { opacity: 0, scale: 0.92, y: 20 },
  visible: { opacity: 1, scale: 1, y: 0, transition: { type: 'spring', stiffness: 300, damping: 28 } },
  exit:    { opacity: 0, scale: 0.94, y: 10, transition: { duration: 0.2 } },
};

export function Contact({ isOpen, onClose }) {
  const {
    form, errors, errorMessage,
    updateField, handleSubmit, reset,
    isLoading, isSuccess, isError,
  } = useContactForm();

  const firstInputRef = useRef(null);

  // Focus trap — auto-focus first field on open
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => firstInputRef.current?.focus(), 100);
    } else {
      // Reset form a moment after close so animation completes
      setTimeout(reset, 300);
    }
  }, [isOpen, reset]);

  // Close on Escape
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    if (isOpen) window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  // Prevent body scroll when modal open
  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  return (
    <>
      {/* Floating Hire Me button */}
      <button
        className="hire-me-fab"
        onClick={onClose === undefined ? undefined : () => {}}
        aria-label="Open Hire Me form"
        id="hire-me-fab-btn"
        style={{ display: 'none' }} /* controlled externally via App */
      />

      {/* Modal */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            className="modal-backdrop"
            variants={backdropVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            transition={{ duration: 0.22 }}
            onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
            role="dialog"
            aria-modal="true"
            aria-labelledby="modal-title"
          >
            <motion.div
              className="modal-box"
              variants={modalVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Close button */}
              <button className="modal-close" onClick={onClose} aria-label="Close dialog">
                <X size={18} strokeWidth={2} />
              </button>

              {/* Header */}
              <div className="modal-header">
                <div className="modal-eyebrow">
                  <Briefcase size={14} strokeWidth={2} />
                  Let's work together
                </div>
                <h2 className="modal-title" id="modal-title">Get In Touch</h2>
                <p className="modal-subtitle">
                  Have a project in mind or want to discuss an opportunity?
                  Drop me a message and I'll get back to you.
                </p>
              </div>

              {/* Success state */}
              {isSuccess ? (
                <motion.div
                  className="form-status success"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.35 }}
                >
                  <CheckCircle size={20} strokeWidth={2} style={{ flexShrink: 0, marginTop: 1 }} />
                  <div>
                    <strong>Message sent!</strong>
                    <br />
                    Thank you for reaching out. I'll get back to you as soon as possible.
                  </div>
                </motion.div>
              ) : (
                <form className="contact-form" onSubmit={handleSubmit} noValidate>
                  {/* Name row */}
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label" htmlFor="firstName">
                        First Name <span className="required" aria-hidden="true">*</span>
                      </label>
                      <input
                        id="firstName"
                        ref={firstInputRef}
                        className={`form-input${errors.firstName ? ' error' : ''}`}
                        type="text"
                        placeholder="Ervin"
                        value={form.firstName}
                        onChange={(e) => updateField('firstName', e.target.value)}
                        aria-required="true"
                        aria-invalid={!!errors.firstName}
                        aria-describedby={errors.firstName ? 'firstName-error' : undefined}
                        disabled={isLoading}
                        autoComplete="given-name"
                      />
                      {errors.firstName && (
                        <span id="firstName-error" className="form-error-msg" role="alert">
                          {errors.firstName}
                        </span>
                      )}
                    </div>
                    <div className="form-group">
                      <label className="form-label" htmlFor="lastName">Last Name</label>
                      <input
                        id="lastName"
                        className="form-input"
                        type="text"
                        placeholder="Tejada"
                        value={form.lastName}
                        onChange={(e) => updateField('lastName', e.target.value)}
                        disabled={isLoading}
                        autoComplete="family-name"
                      />
                    </div>
                  </div>

                  {/* Email */}
                  <div className="form-group">
                    <label className="form-label" htmlFor="email">
                      Email Address <span className="required" aria-hidden="true">*</span>
                    </label>
                    <input
                      id="email"
                      className={`form-input${errors.email ? ' error' : ''}`}
                      type="email"
                      placeholder="you@example.com"
                      value={form.email}
                      onChange={(e) => updateField('email', e.target.value)}
                      aria-required="true"
                      aria-invalid={!!errors.email}
                      aria-describedby={errors.email ? 'email-error' : undefined}
                      disabled={isLoading}
                      autoComplete="email"
                    />
                    {errors.email && (
                      <span id="email-error" className="form-error-msg" role="alert">
                        {errors.email}
                      </span>
                    )}
                  </div>

                  {/* Phone */}
                  <div className="form-group">
                    <label className="form-label" htmlFor="phone">Phone (optional)</label>
                    <input
                      id="phone"
                      className="form-input"
                      type="tel"
                      placeholder="+63 900 000 0000"
                      value={form.phone}
                      onChange={(e) => updateField('phone', e.target.value)}
                      disabled={isLoading}
                      autoComplete="tel"
                    />
                  </div>

                  {/* Message */}
                  <div className="form-group">
                    <label className="form-label" htmlFor="message">
                      Message <span className="required" aria-hidden="true">*</span>
                    </label>
                    <textarea
                      id="message"
                      className={`form-textarea${errors.message ? ' error' : ''}`}
                      placeholder="Tell me about the project or opportunity..."
                      value={form.message}
                      onChange={(e) => updateField('message', e.target.value)}
                      rows={5}
                      aria-required="true"
                      aria-invalid={!!errors.message}
                      aria-describedby={errors.message ? 'message-error' : undefined}
                      disabled={isLoading}
                    />
                    {errors.message && (
                      <span id="message-error" className="form-error-msg" role="alert">
                        {errors.message}
                      </span>
                    )}
                  </div>

                  {/* Global error */}
                  {isError && (
                    <div className="form-status error" role="alert">
                      <AlertCircle size={18} strokeWidth={2} style={{ flexShrink: 0, marginTop: 1 }} />
                      <span>{errorMessage}</span>
                    </div>
                  )}

                  {/* Submit */}
                  <button
                    type="submit"
                    className="submit-btn"
                    disabled={isLoading}
                    aria-busy={isLoading}
                  >
                    {isLoading ? (
                      <>
                        <Loader2 size={16} strokeWidth={2} style={{ animation: 'spin 1s linear infinite' }} />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send size={16} strokeWidth={2} />
                        Send Message
                      </>
                    )}
                  </button>
                </form>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </>
  );
}
